/**
 * Undo of the last Database import.
 *
 * Before an apply runs, the importer's `snapshot(plan, { mode })` returns every
 * document the apply will create, overwrite or delete, as
 * `[{ collection, id, data }]` (`data` = the CURRENT document data, or null
 * when the document does not exist yet). It is stored as:
 *
 *   import_undo/<entryId>                       // entryId = the import_log_entries id of the import
 *     { table, mode, fileName, user, at, docs, creates, chunks, status, last_updated }
 *   import_undo/<entryId>/chunks/<i>            // { items: [{ collection, id, data }] }
 *
 * `status`: 'ready' (undo possible), 'undone', 'no_snapshot' (the snapshot
 * could not be written; the apply still ran) or 'expired' (kept for older
 * snapshots; only the latest snapshot per table is kept, the previous one is
 * deleted with its chunks once the new import is in the log).
 *
 * Right after the apply (also after one that failed part-way), `saveAfterState`
 * stores on every chunk `after: [fingerprint per item]` = the documents as the
 * import left them, and `after_saved: true` on the entry.
 *
 * `undo(entryId)` writes back (FULL set, not a merge) only the documents whose
 * current fingerprint still equals `after`, deletes the unchanged ones the
 * import had created, and keeps + lists every document changed since the import
 * (engine merges, edits in the app). It then records an `undo` entry in the
 * import log so the freshness badge and the history of the table show it.
 * Only the latest import of a table can be undone (see undoGuard).
 *
 * Only the staff import (flags off) keeps a snapshot here: the fleet file and the Invygo sheet, whose documents lived
 * in the engine's store under USE_CONTRACTS_V2, are no longer imported by hand (owner, 28 Sep 2026), and the staff
 * import on the store has its own undo (staffImporter `storeUndo`).
 */
import { db, auth } from '../firebase';
import {
    collection, doc, getDoc, getDocs, query, where, documentId, writeBatch, setDoc, serverTimestamp
} from 'firebase/firestore';
import logger from '../utils/logger';
import { importLogService } from './importLogService';
import { chunkSnapshotItems, normalizeSnapshotItems, countCreates, undoGuard, afterStateOf, undoPlan, itemKey } from './importers/importUndo';

export const UNDO_COLLECTION = 'import_undo';
export const UNDO_CHUNKS = 'chunks';

/** Documents per writeBatch when restoring (the Firestore limit is 500). */
const RESTORE_BATCH_SIZE = 400;
/** Ids per `documentId() in` query when reading the current documents. */
const READ_CHUNK_SIZE = 30;

const chunkDoc = (entryId, i) => doc(db, UNDO_COLLECTION, entryId, UNDO_CHUNKS, String(i));
const undoDoc = (entryId) => doc(db, UNDO_COLLECTION, entryId);

const uniqueIds = (ids) => Array.from(new Set((ids || []).map(id => (id === undefined || id === null ? '' : String(id))).filter(Boolean)));

/**
 * Current documents of `ids` in `collectionName` as snapshot items
 * `[{ collection, id, data }]`, read 30 ids per `documentId() in` query.
 * Ids with no document get `data: null` (the apply will create them).
 */
export const snapshotItemsFor = async (collectionName, ids) => {
    const wanted = uniqueIds(ids);
    if (wanted.length === 0) return [];
    const found = new Map();
    for (let i = 0; i < wanted.length; i += READ_CHUNK_SIZE) {
        const chunk = wanted.slice(i, i + READ_CHUNK_SIZE);
        const snap = await getDocs(query(collection(db, collectionName), where(documentId(), 'in', chunk)));
        snap.forEach(d => found.set(d.id, d.data()));
    }
    return wanted.map(id => ({ collection: collectionName, id, data: found.has(id) ? found.get(id) : null }));
};

/**
 * Current data of every item's document, as a Map `collection/id` -> data (a
 * document that does not exist is left out), read 30 ids per query.
 */
const currentDocsOf = async (items) => {
    const idsByCollection = new Map();
    (items || []).forEach(item => {
        if (!idsByCollection.has(item.collection)) idsByCollection.set(item.collection, []);
        idsByCollection.get(item.collection).push(item.id);
    });
    const out = new Map();
    for (const [collectionName, ids] of idsByCollection) {
        const current = await snapshotItemsFor(collectionName, ids);
        current.forEach(c => {
            if (c.data !== null && c.data !== undefined) out.set(itemKey(c), c.data);
        });
    }
    return out;
};

/** Snapshot items from documents already read by the plan (Map id -> data), no extra reads. */
export const snapshotItemsFromMap = (collectionName, ids, existingById) => uniqueIds(ids).map(id => ({
    collection: collectionName,
    id,
    data: existingById && existingById.has(id) ? existingById.get(id) : null
}));

/**
 * Drops a snapshot: its chunk documents (batched deletes) and then the entry
 * document itself. A rule set that only allows validated writes on
 * `import_undo/<id>` refuses the delete of the entry document; it is then
 * marked `expired` instead, which the undo guard refuses just the same.
 */
const deleteSnapshot = async (entryId, chunkCount) => {
    let ids = [];
    if (Number(chunkCount) > 0) {
        ids = Array.from({ length: Number(chunkCount) }, (_, i) => String(i));
    } else {
        const snap = await getDocs(collection(db, UNDO_COLLECTION, entryId, UNDO_CHUNKS));
        ids = snap.docs.map(d => d.id);
    }
    for (let i = 0; i < ids.length; i += RESTORE_BATCH_SIZE) {
        const batch = writeBatch(db);
        ids.slice(i, i + RESTORE_BATCH_SIZE).forEach(id => batch.delete(doc(db, UNDO_COLLECTION, entryId, UNDO_CHUNKS, id)));
        await batch.commit();
    }
    try {
        const batch = writeBatch(db);
        batch.delete(undoDoc(entryId));
        await batch.commit();
    } catch (error) {
        logger.warn('[importUndo] entry delete refused, marking it expired instead:', error?.code || error);
        await setDoc(undoDoc(entryId), { status: 'expired', chunks: 0, last_updated: serverTimestamp() }, { merge: true });
    }
};

/** Only the latest snapshot of a table is kept: drop every other one. */
const pruneOtherSnapshots = async (table, keepId) => {
    const snap = await getDocs(query(collection(db, UNDO_COLLECTION), where('table', '==', table)));
    for (const d of snap.docs) {
        if (d.id === keepId) continue;
        await deleteSnapshot(d.id, d.data()?.chunks);
    }
};

export const importUndoService = {
    /** A fresh `import_log_entries` id, claimed before the apply so the snapshot can be stored under it. */
    newEntryId() {
        return doc(collection(db, 'import_log_entries')).id;
    },

    /** `import_undo/<entryId>` as `{ id, ...data }`, or null. */
    async getEntry(entryId) {
        if (!entryId) return null;
        try {
            const snap = await getDoc(undoDoc(entryId));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        } catch (error) {
            logger.error('[importUndo] read failed:', error);
            return null;
        }
    },

    /** The snapshot of the latest import of a table, or null when there is none / it is not usable. */
    async getReadyEntryFor(table, log) {
        const entryId = log?.entry_id;
        if (!table || !entryId) return null;
        const entry = await this.getEntry(entryId);
        return entry && undoGuard({ entryId, entry, log }).allowed ? entry : null;
    },

    /**
     * Writes the snapshot of one import BEFORE its apply runs. Throws when the
     * snapshot cannot be written (the caller still applies, and marks the entry
     * `no_snapshot`). The previous snapshot of the table is NOT dropped here:
     * the apply may still fail before writing anything, and then the previous
     * import must stay undoable (see pruneOthers).
     * Returns the entry with `chunks` = the item chunks as written (saveAfterState needs them).
     */
    async saveSnapshot({ entryId, table, mode, fileName, items }, onProgress) {
        const normalized = normalizeSnapshotItems(items);
        const chunks = chunkSnapshotItems(normalized);
        const report = (done, label) => { if (onProgress) onProgress({ done, total: chunks.length + 1, label }); };

        for (let i = 0; i < chunks.length; i++) {
            report(i, `Saving undo snapshot ${i + 1} of ${chunks.length}`);
            await setDoc(chunkDoc(entryId, i), { items: chunks[i] });
        }
        const entry = {
            table,
            mode: mode || null,
            fileName: fileName || null,
            user: auth.currentUser?.email || null,
            at: new Date().toISOString(),
            docs: normalized.length,
            creates: countCreates(normalized),
            chunks: chunks.length,
            status: 'ready',
            last_updated: serverTimestamp()
        };
        report(chunks.length, 'Undo snapshot saved');
        await setDoc(undoDoc(entryId), entry);
        return { ...entry, id: entryId, at: entry.at, chunks, chunkCount: chunks.length };
    },

    /**
     * Right after the apply (successful or not): the fingerprint of every
     * snapshot document as the import left it, stored per chunk, so the undo
     * can skip what changed afterwards. Returns `{ changed }` = how many
     * documents the apply really changed (0 = it wrote nothing). Throws when the
     * documents cannot be read or the fingerprints cannot be stored (the undo
     * then stays refused by undoGuard).
     */
    async saveAfterState(entryId, chunks) {
        const current = await currentDocsOf((chunks || []).flat());
        const { after, changed } = afterStateOf(chunks, current);
        for (let i = 0; i < after.length; i++) {
            await setDoc(chunkDoc(entryId, i), { after: after[i] }, { merge: true });
        }
        await setDoc(undoDoc(entryId), { after_saved: true, changed, last_updated: serverTimestamp() }, { merge: true });
        return { changed };
    },

    /**
     * Keeps only the latest snapshot per table: called once the import log names
     * `keepId`, when every older snapshot can no longer be undone anyway.
     */
    async pruneOthers(table, keepId) {
        try {
            await pruneOtherSnapshots(table, keepId);
        } catch (error) {
            logger.warn('[importUndo] could not delete the previous snapshot:', error);
        }
    },

    /** Marks an entry whose snapshot could not be written, so the card can say undo is unavailable. */
    async markNoSnapshot({ entryId, table, mode, fileName, error }) {
        try {
            await setDoc(undoDoc(entryId), {
                table,
                mode: mode || null,
                fileName: fileName || null,
                user: auth.currentUser?.email || null,
                at: new Date().toISOString(),
                docs: 0,
                creates: 0,
                chunks: 0,
                status: 'no_snapshot',
                error: error ? String(error.message || error).slice(0, 300) : null,
                last_updated: serverTimestamp()
            });
        } catch (e) {
            logger.error('[importUndo] could not mark the entry as no_snapshot:', e);
        }
    },

    /**
     * Restores the documents of the snapshot that nobody changed since the
     * import: a full `set` (not a merge) for the ones that existed before, a
     * `delete` for the ones it created. A document changed after the import is
     * kept as it is and listed in `skippedDocs`. Refuses when the import is not
     * the latest one of its table or was already undone.
     * `onProgress({ done, total, label })`.
     * Returns `{ success, restored, deleted, skipped, skippedDocs, table, collections }`.
     */
    async undo(entryId, onProgress) {
        const report = (done, total, label) => { if (onProgress) onProgress({ done, total: total || 1, label }); };
        const entry = await this.getEntry(entryId);
        const log = entry?.table ? await importLogService.getLatest(entry.table) : null;
        const guard = undoGuard({ entryId, entry, log });
        if (!guard.allowed) return { success: false, error: guard.reason };

        try {
            report(0, entry.docs, 'Reading the undo snapshot...');
            const snap = await getDocs(collection(db, UNDO_COLLECTION, entryId, UNDO_CHUNKS));
            const items = [];
            snap.forEach(d => {
                const data = d.data() || {};
                const after = Array.isArray(data.after) ? data.after : [];
                (data.items || []).forEach((item, i) => items.push({ ...item, after: after[i] }));
            });
            if (items.length === 0) return { success: false, error: 'The undo snapshot is empty; nothing was restored.' };

            // Only what is still exactly as the import left it goes back (ADM-04): an engine
            // merge or an edit made after the import is kept, not overwritten with old data.
            report(0, items.length, 'Checking which documents changed since the import...');
            const current = await currentDocsOf(items);
            const plan = undoPlan(items, current);
            const writes = [...plan.restore, ...plan.remove];

            let restored = 0;
            let deleted = 0;
            for (let i = 0; i < writes.length; i += RESTORE_BATCH_SIZE) {
                const chunk = writes.slice(i, i + RESTORE_BATCH_SIZE);
                report(i, writes.length, `Restoring ${i + 1}-${Math.min(i + RESTORE_BATCH_SIZE, writes.length)} of ${writes.length}`);
                const batch = writeBatch(db);
                for (const item of chunk) {
                    const ref = doc(db, item.collection, item.id);
                    if (item.data === null || item.data === undefined) { batch.delete(ref); deleted++; }
                    else {
                        // Full replace, not a merge: the document goes back to exactly what it was.
                        // A legacy document without `last_updated` gets one, otherwise the security
                        // rules (isValidData) refuse the write and the whole batch fails.
                        const data = item.data.last_updated === undefined
                            ? { ...item.data, last_updated: new Date().toISOString() }
                            : item.data;
                        batch.set(ref, data);
                        restored++;
                    }
                }
                await batch.commit();
            }
            report(writes.length, writes.length || 1, 'Recording the undo...');

            const skipped = plan.skipped.length;
            await setDoc(undoDoc(entryId), {
                status: 'undone',
                undone_at: new Date().toISOString(),
                undone_by: auth.currentUser?.email || null,
                skipped,
                skipped_ids: plan.skipped.slice(0, 50).map(s => `${s.collection}/${s.id}`),
                last_updated: serverTimestamp()
            }, { merge: true });

            // The table log gets an `undo` entry: the freshness badge and the history show it.
            await importLogService.record({
                table: entry.table,
                mode: 'undo',
                fileName: entry.fileName || null,
                counts: { restored, deleted, keptChanged: skipped }
            });

            const collections = Array.from(new Set(items.map(item => item.collection)));

            logger.info(`[importUndo] ${entry.table}: ${restored} document(s) restored, ${deleted} deleted, ${skipped} kept (changed since the import)`);
            return { success: true, restored, deleted, skipped, skippedDocs: plan.skipped, table: entry.table, collections };
        } catch (error) {
            logger.error('[importUndo] undo failed:', error);
            return { success: false, error: error?.message || String(error) };
        }
    }
};

export default importUndoService;
